import { Component } from "react"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* Switch */
import Switch from "react-switch"

/* Components */
import { Input } from "../../components"

/* Icons */
import { EyeIcon, PlayersIcon, LockIcon, ClockIcon, PlayIcon } from "../../components/Icons"

/* Confirm & Toast */
import { pushConfirm } from "../../components/Confirm"
import { pushToast } from "../../components/Toast"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { get, setParameters } from "../../api/Table"

/* Helpers */
import { utils } from "../../helpers"

/* Constants */
import { env } from "../../constants"

/* Widgets */
import Layout from "./Layout"

/* Swal */
import Swal from "../../helpers/alert"

/* SOCKET.IO */
import { io } from "socket.io-client"

/* JWT */
import { decodeToken } from "react-jwt"



/* Widget Control */
class Control extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            visible: false,
            available: false,
            closed: false,
            open24Hours: false,
            workStartTime: "",
            workEndTime: "",
            waiting: false,
        }

        this.bridge = null
    }

    componentDidMount = () => {

        /* Load Data */
        this.load()

        /* Connect to Bridge */
        this.connect()
    }

    componentWillUnmount = () => {
        if (this.bridge) {
            this.bridge.disconnect()
        }
    }

    /* Load Table Data */
    load = (reload = false) => {

        /* Fields */
        const { uid } = this.props

        /* Check Params */
        if (!uid) {
            this.setState({ status: "error" })
            return
        }

        /* Refresh the page if necessary */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* START REST API */
        get({ uid }).then((response) => {
            if (response.status) {

                const data = response.data

                this.setState({
                    status: "ok",
                    visible: utils.checkBoolean(data.visible),
                    available: utils.checkBoolean(data.available),
                    closed: utils.checkBoolean(data.closed),
                    open24Hours: utils.checkBoolean(data.open24Hours),
                    workStartTime: utils.checkValue(data.workStartTime, ""),
                    workEndTime: utils.checkValue(data.workEndTime, ""),
                })

            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission" })
            }
            else if (response.status === 498) {
                this.setState({ status: "key" })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => {
            this.setState({ status: "network" })
        })
    }

    /* Connect to Bridge */
    connect = () => {

        /* Get Token */
        const token = localStorage.getItem("token")
        const decoded = decodeToken(token)

        this.bridge = io(env.bridgeURL, { auth: { token: `${decoded.token}`, type: "admin" } })

        /* On Connect */
        this.bridge.on("connect", () => {
            Swal.fire(utils.notification(t("control.bridgeConnected"), "info"))
        })

        /* On Connect */
        this.bridge.on("connect_error", () => {
            Swal.fire(utils.notification(t("control.bridgeError"), "error"))
        })

    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { visible, available, closed, open24Hours, workStartTime, workEndTime } = this.state
        const { uid, socket } = this.props

        if (!socket || !socket.connect) {
            Swal.fire(utils.notification(t("control.noGameConnection"), "error"))
            return
        }

        /* Check Working Times */
        if (!open24Hours) {

            /* Check Working Start Time */
            if (workStartTime.length !== 5) {
                this._workStartTime.error(t("control.enterTime"))
                return
            }

            /* Check Working End Time */
            if (workEndTime.length !== 5) {
                this._workEndTime.error(t("control.enterTime"))
                return
            }
        }

        /* Check Booleand */
        const check = (value) => value ? 1 : 0

        /* Prepare Data */
        const predata = {
            uid,
            visible: check(visible),
            available: check(available),
            closed: check(closed),
            open24Hours: check(open24Hours),
            workStartTime,
            workEndTime,
        }

        /* Start Loading */
        this.setState({ waiting: true })

        /* Send to REST API */
        setParameters(predata).then((response) => {
            if (response.status === 200) {
                socket.emit("tableClosed", { value: closed })

                if (this.bridge) {
                    this.bridge.emit("updateData", uid)
                }

                Swal.fire(utils.notification(t("swal.savedSuccess"), "success", 3000))
            }
            else if (response.status === 401 || response.status === 403) {
                Swal.fire(utils.notification(t("swal.noAccess"), "error"))
            }
            else if (response.status === 498) {
                Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
            }
            else {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })
    }

    /* CONTINUE THE GAME — the internet dropped, the dealer monitor lost the game and the round ended
       without it, so the monitor kept standing on pause. The round is over and has no number to
       continue, so the continue goes to the table as a whole: the game takes the pause off itself
       and from the dealer monitor ("monitorContinue") */
    continueGame = () => {

        /* Fields */
        const { socket } = this.props

        /* Check Connection */
        if (!socket || !socket.connected) {
            pushToast({ text: t("control.noGameConnection"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.continueConfirm"),
            text: t("control.continueText"),
            icon: "warning",
            confirmText: t("control.continueGame"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Check Connection — it may have dropped while the question was open */
            if (!socket.connected) {
                pushToast({ text: t("control.noGameConnection"), type: "error" })
                return
            }

            socket.emit("continue", { type: "continue" })
            pushToast({ text: t("widget.gameContinued"), type: "success" })
        })
    }

    /* Draw Button */
    _button = () => {

        /* Fields */
        const { waiting } = this.state

        /* Check Loading */
        if (waiting) {
            return <div className="tbl--button primary disabled"><LoadingOutlined /></div>
        }

        return <div onClick={() => this.save()} className="tbl--button primary">{t("common.save")}</div>
    }

    /* Draw One Switch Row */
    _row = (icon, title, hint, node) => {
        return (
            <div className="tbl--row">

                <div className="tbl--row-icon">{icon}</div>

                <div className="tbl--row-text">
                    <h3>{title}</h3>
                    <p>{hint}</p>
                </div>

                <div className="tbl--row-action">{node}</div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, visible, available, closed, open24Hours, workStartTime, workEndTime } = this.state

        /* Layout */
        if (status !== "ok") {
            return <Layout status={status} reload={() => this.load(true)} />
        }

        return (
            <div className="tbl--panel">

                {/* State Of The Table */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <EyeIcon size={14} />
                        {t("control.stateSection")}
                    </div>

                    {/* Visible */}
                    {this._row(
                        <EyeIcon size={16} />,
                        t("control.tableVisibility"),
                        t("control.makePublicHidden"),
                        <Switch uncheckedIcon={false} checkedIcon={false} onColor="#8bc34a" onChange={(value) => this.setState({ visible: value })} checked={visible} />,
                    )}

                    {/* Available */}
                    {this._row(
                        <PlayersIcon size={16} />,
                        available ? t("control.tableFree") : t("control.tableBusy"),
                        t("control.autoDetect"),
                        <div className={`tbl--state ${available ? "green" : "amber"}`}>{available ? t("control.tableFree") : t("control.tableBusy")}</div>,
                    )}

                    {/* Closed */}
                    {this._row(
                        <LockIcon size={16} />,
                        t("control.tableAccess"),
                        t("control.allowDeny"),
                        <div className="tbl--row-switch">
                            <div className={`tbl--state ${closed ? "red" : "green"}`}>{closed ? t("control.tableClosed") : t("control.tableOpen")}</div>
                            <Switch uncheckedIcon={false} checkedIcon={false} onColor="#f44336" onChange={(value) => this.setState({ closed: value })} checked={closed} />
                        </div>,
                    )}

                </div>


                {/* Working Hours */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <ClockIcon size={14} />
                        {t("control.timeSection")}
                    </div>

                    {/* Open 24 Hours */}
                    {this._row(
                        <ClockIcon size={16} />,
                        t("control.allDay"),
                        t("control.allDayWork"),
                        <Switch uncheckedIcon={false} checkedIcon={false} onColor="#8bc34a" onChange={(value) => this.setState({ open24Hours: value })} checked={open24Hours} />,
                    )}

                    {/* Working Times */}
                    <div className={`tbl--inputs ${open24Hours ? "disabled" : ""}`}>
                        <Input ref={(ref) => this._workStartTime = ref} label={t("control.workStart")} mask="99:99" placeholder="__:__" disabled={open24Hours} value={workStartTime} onChange={(event) => this.setState({ workStartTime: event.target.value })} />
                        <Input ref={(ref) => this._workEndTime = ref} label={t("control.workEnd")} mask="99:99" placeholder="__:__" disabled={open24Hours} value={workEndTime} onChange={(event) => this.setState({ workEndTime: event.target.value })} />
                    </div>

                </div>


                {/* Game */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <PlayIcon size={14} />
                        {t("control.gameSection")}
                    </div>

                    {/* Continue */}
                    {this._row(
                        <PlayIcon size={16} />,
                        t("control.continueGame"),
                        t("control.continueHint"),
                        <div onClick={() => this.continueGame()} className="tbl--button ghost tbl--row-button">
                            <PlayIcon size={15} />
                            {t("control.continueButton")}
                        </div>,
                    )}

                </div>


                <div className="tbl--footer">
                    {this._button()}
                </div>

            </div>
        )
    }
}

export default Control